/**
 * Notifications API — fetch, mark read, combined unread count.
 * Uses RPC functions for efficient paginated queries.
 */

import { z } from 'zod'
import { supabase } from '@turnifly/shared/api'

// ── Schemas ─────────────────────────────────────────────────

const NotificationActorSchema = z.object({
  id: z.string(),
  name: z.string(),
  avatar_url: z.string().nullable().optional(),
})

export const NotificationItemSchema = z.object({
  id: z.string().uuid(),
  type: z.string(),
  title: z.string(),
  body: z.string().nullable(),
  actors: z.array(NotificationActorSchema).default([]),
  actors_count: z.number().default(1),
  target_type: z.string().nullable(),
  target_id: z.string().nullable(),
  is_read: z.boolean(),
  created_at: z.string(),
})

export type NotificationItem = z.infer<typeof NotificationItemSchema>

export const CombinedUnreadSchema = z.object({
  messages: z.number(),
  notifications: z.number(),
  total: z.number(),
})

export type CombinedUnread = z.infer<typeof CombinedUnreadSchema>

// ── Fetch notifications ─────────────────────────────────────

export async function fetchNotifications(
  userId: string,
  type?: string,
  limit = 30,
  offset = 0,
): Promise<NotificationItem[]> {
  const { data } = await supabase
    .rpc('get_notifications_list', {
      p_user_id: userId,
      p_type: type ?? null,
      p_limit: limit,
      p_offset: offset,
    })
    .throwOnError()

  return ((data ?? []) as unknown[]).map((row) => NotificationItemSchema.parse(row))
}

// ── Mark notifications read ─────────────────────────────────

export async function markNotificationRead(notificationId: string): Promise<void> {
  await supabase
    .from('notifications')
    .update({ is_read: true })
    .eq('id', notificationId)
    .throwOnError()
}

export async function markAllNotificationsRead(userId: string): Promise<void> {
  await supabase
    .from('notifications')
    .update({ is_read: true })
    .eq('user_id', userId)
    .eq('is_read', false)
    .throwOnError()
}

// ── Combined unread count ───────────────────────────────────

export async function fetchCombinedUnreadCount(
  userId: string,
): Promise<CombinedUnread> {
  const { data } = await supabase
    .rpc('get_combined_unread_count', { p_user_id: userId })
    .throwOnError()

  return CombinedUnreadSchema.parse(data)
}
